import * as PIXI from 'pixi.js';
import * as PrefabManager from '../PrefabManager';
import {PIDController} from '../misc/PIDController'
import { Settings } from '../../Settings';
import { normalizePI } from '../../b2Editor/utils/extramath';
import * as emitterManager from '../../utils/EmitterManager';
import { Explosive } from './Explosive';
import * as AudioManager from '../../utils/AudioManager';
import { mapPointThroughPortal, rayCastThroughPortals } from '../guns/portalPhysicsQuery';
import { portalTangent, portalTangentDistance, signedPortalDistance } from '../guns/portalMath';
import { endpointUsesHostFixture, pointWithinPortalAperture } from '../guns/portalCollision';
import { linkedPortals } from '../guns/PortalBridge';

import {
    game
} from "../../Game";
import { b2AddVec2, b2Clamp, b2CloneVec2, b2DotVV, b2MulVec2, b2SubVec2 } from '../../../libs/debugdraw';
const { getPointer, NULL } = Box2D; // emscriptem specific


const SIDE_RIGHT = 0;
const SIDE_LEFT = 1;
// How far from the rims of a portal's opening a drone flies through it, in metres.
const PORTAL_CLEARANCE = 2.5;
const RIGHTING_STIFFNESS = 40;
const RIGHTING_DAMPING = 12;

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();
const vec3 = new Box2D.b2Vec2();
const vec4 = new Box2D.b2Vec2();
const vec5 = new Box2D.b2Vec2();
const vec6 = new Box2D.b2Vec2();
const vec7 = new Box2D.b2Vec2();

export class DroneBomb extends Explosive {
    constructor(target) {
		super(target);
		this.isDrone = true;

		this.throttleController = new PIDController(2, 0, 1);
		this.horizontalThrottle = new PIDController(2, 0, 1);


		this.rayCallback = Object.assign(new Box2D.JSRayCastCallback(), {
			ReportFixture: function (fixture_p, point_p, normal_p, fraction) {

				const fixture = Box2D.wrapPointer(fixture_p, Box2D.b2Fixture);
				// const point = Box2D.wrapPointer(point_p, Box2D.b2Vec2);
				// const normal = Box2D.wrapPointer(normal_p, Box2D.b2Vec2);

				if(fixture.GetBody() === self.body) return -1;
				this.m_fixture = fixture;
				this.m_hit = true;
				return fraction;
			},
			m_hit: false
		});

		const self = this;

    }
    init(){
        super.init();
		this.body = this.lookupObject['body'];

		this.droneTexture = this.body.myTexture.children[1];
		this.lightTexture = this.body.myTexture.children[0];

		this.explodeTarget = this.body;

		this.sides = [];

		this.emitterLeft = emitterManager.getLoopingEmitter("sparksMetal", this.body, this.body.GetPosition(), 0);
		this.emitterRight = emitterManager.getLoopingEmitter("sparksMetal", this.body, this.body.GetPosition(), 0);
		this.emitterLeft.emit = false;
		this.emitterRight.emit = false;

		for (let fixture = this.body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
			if (fixture.GetShape().GetType() === Box2D.b2Shape.e_circle) {
				fixture.SetSensor(true);
				this.sides.push(fixture);
			}
		}


		this.body.SetAngularDamping(0.8);
		this.body.SetLinearDamping(0.1);

		this.blinkTimer = -0.01;
		this.blinkDelay = 0;
		this.blink = false;

		this.chaseTarget = null;
		this.chasePortal = null;
		this.righting = false;
		this.chaseScoutTimer = 0;
		this.chaseScoutDelay = 100;
		this.lookRange = this.prefabObject.settings.lookRange;
		this.lookRangeVec = new Box2D.b2Vec2(this.lookRange, this.lookRange)
		this.startPosition = b2CloneVec2(this.body.GetPosition());
		this.wayPoint = null;
	}

	explode(){
		super.explode();
	}
	performExplosion(){
		if(this.exploded || this.destroyed) return;
		super.performExplosion();

		const pos = this.body.GetPosition();
		AudioManager.playSFX('drone-explosion', 0.3, 1.0 + 0.4 * Math.random()-0.2, pos);

		this.destroy();
	}

	update(){
		super.update();

		if(this.destroyed) return;

		const forceMultiplier = 100 / this.prefabObject.settings.flySpeed;
		const maxForce = 100 / forceMultiplier;

		const gravityNormal = vec2;
		gravityNormal.Set(game.world.GetGravity().x, game.world.GetGravity().y);
		gravityNormal.Normalize();

		const targetPosition = vec1;
		if(this.chasePortal && !this.portalOpen(this.chasePortal)) this.chasePortal = null;
		if(this.chaseTarget && this.chasePortal){
			const point = this.portalChasePoint();
			targetPosition.Set(point.x, point.y);
		}else if(this.chaseTarget){
			targetPosition.Set(this.chaseTarget.GetPosition().x, this.chaseTarget.GetPosition().y);
		}else if(this.wayPoint){
			targetPosition.Set(this.wayPoint.x, this.wayPoint.y);
		}else{
			targetPosition.Set(this.startPosition.x, this.startPosition.y);
		}

		const gravityNormalIncrease = vec3;
		gravityNormalIncrease.Set(gravityNormal.x, gravityNormal.y);
		b2MulVec2(gravityNormalIncrease, -8.0 * forceMultiplier);

		b2AddVec2(targetPosition, gravityNormalIncrease);
		b2SubVec2(targetPosition, this.body.GetPosition());
		b2SubVec2(targetPosition, this.body.GetLinearVelocity());

		const targetDistance = targetPosition;

		this.throttleController.setError(  b2DotVV(targetDistance, gravityNormal));
		this.throttleController.step( 1 / Settings.physicsTimeStep );
		let targetThrottle = b2Clamp(this.throttleController.getOutput() / forceMultiplier, -maxForce, maxForce);

		const gravityAngle = Math.atan2(game.world.GetGravity().get_y(), game.world.GetGravity().get_x());
		const forceDirection = normalizePI(gravityAngle -Math.PI);
		const forceNormal = vec4;
		forceNormal.Set(Math.cos(forceDirection), Math.sin(forceDirection));
		const facingGravity = b2DotVV(gravityNormal,forceNormal);
		const almostFlipped = 0;

		if(facingGravity<almostFlipped){
			targetThrottle *= -1;
		}

		const force = vec5;
		force.Set(0, 0);
		force.x = targetThrottle * Math.cos(forceDirection);
		force.y = targetThrottle * Math.sin(forceDirection);

		const quadNormal = vec6;
		quadNormal.Set(Math.cos(this.body.GetAngle()), Math.sin(this.body.GetAngle()));

		this.horizontalThrottle.setError(b2DotVV(targetDistance,quadNormal));
		this.horizontalThrottle.step( 1 / Settings.physicsTimeStep );
		let targetHorizontalForce = b2Clamp(this.horizontalThrottle.getOutput() / forceMultiplier, -maxForce, maxForce);

		const horizontalForce = vec7;
		horizontalForce.Set(0, 0);
		horizontalForce.x = targetHorizontalForce * Math.cos(this.body.GetAngle());
		horizontalForce.y = targetHorizontalForce * Math.sin(this.body.GetAngle());

		this.body.ApplyForce(horizontalForce, this.body.GetPosition(), true);
		this.body.ApplyForce(force, this.body.GetPosition(), true);

		if(this.righting){
			const tilt = normalizePI(gravityAngle - Settings.pihalve - this.body.GetAngle());
			const spin = this.body.GetAngularVelocity();
			if(Math.abs(tilt) < 0.05 && Math.abs(spin) < 0.5) this.righting = false;
			else this.body.ApplyTorque(this.body.GetInertia() * (RIGHTING_STIFFNESS * tilt - RIGHTING_DAMPING * spin), true);
		}

		this.positionEmitters();
		this.emitterLeft.update(game.editor.deltaTime * 0.001);
		this.emitterRight.update(game.editor.deltaTime * 0.001);


		if(this.active){
			if (PrefabManager.timerReady(this.blinkTimer, this.blinkDelay, true)) {
				if(!this.blink) this.droneTexture.texture = PIXI.Texture.from(this.droneTexture.texture.label.replace("_off", "_on"));
				else{
					this.droneTexture.texture = PIXI.Texture.from(this.droneTexture.texture.label.replace("_on", "_off"));
					AudioManager.playSFX('drone-beep', 0.1, 1.0, this.body.GetPosition());
				}
				this.blink = !this.blink;
				this.blinkDelay = (this.explodeDelay-this.explodeTimer) * 0.1;
				this.blinkTimer = 0;
			}
			this.blinkTimer += game.editor.deltaTime;
		}

		if (PrefabManager.timerReady(this.chaseScoutTimer, this.chaseScoutDelay, true)) {

			const lowerBound = vec1;
			lowerBound.Set(this.body.GetPosition().x, this.body.GetPosition().y);
			b2SubVec2(lowerBound, this.lookRangeVec);

			const upperBound = vec2;
			upperBound.Set(this.body.GetPosition().x, this.body.GetPosition().y);
			b2AddVec2(upperBound, this.lookRangeVec)

			const bodies = game.editor.queryWorldForBodies(lowerBound, upperBound);

			let found = false;
			this.lightTexture.tint = 0xFFFFFF;
			for(let i = 0; i<bodies.length; i++){
				const body = bodies[i];
				this.rayCallback.m_hit = false;
				this.rayCallback.m_fixture = null;
				if(this.isChaseTarget(body)){

					game.world.RayCast(this.rayCallback, this.body.GetPosition(), body.GetPosition());
					if (this.rayCallback.m_hit){
						const hitFixture = this.rayCallback.m_fixture.GetBody();
						if(hitFixture.mySprite && body.mySprite && hitFixture.mySprite.data.prefabInstanceName === body.mySprite.data.prefabInstanceName){
							this.chaseTarget = body;
							this.chasePortal = null;
							found = true;
						}
					}
					break;
				}
			}
			if(!found) found = this.scoutThroughPortals(bodies);
			if(found){
				this.lightTexture.tint = 0xFF0000;
			}
			if(!found && this.chaseTarget){
				this.chaseTarget = null;
				this.chasePortal = null;
				this.startPosition = b2CloneVec2(this.body.GetPosition());
			}

			this.chaseScoutTimer = 0;
		}
		if(this.prefabObject.settings.chase != DroneBomb.chaseTypes.none){
			this.chaseScoutTimer += game.editor.deltaTime;
		}
		
	}

	isChaseTarget(body){
		const chase = this.prefabObject.settings.chase;
		return (chase === DroneBomb.chaseTypes.mainCharacter && body.mainCharacter) || (chase === DroneBomb.chaseTypes.anyCharacter && body.isHumanoid);
	}

	// Looks through the linked portals in range, along the same rays a laser takes
	// through them. A target seen there is chased into the entrance.
	scoutThroughPortals(bodies){
		// Halfway through the portal it chases through, a drone keeps going.
		if(this.chasePortal && this.body._portalTransitCandidate?.source === this.chasePortal.entrance) return true;
		const position = this.body.GetPosition();
		const drone = {x: position.x, y: position.y};
		for(const portal of bodies){
			const entrance = portal.portalEndpoint;
			const exit = entrance?.bridge?.otherEndpoint(entrance);
			if(!exit || !this.portalOpen({entrance, exit})) continue;
			if(signedPortalDistance(drone, entrance.body.GetPosition(), entrance.body.GetAngle()) <= 0) continue;
			// The look range around the spot the drone would be at on the far side.
			const lookout = mapPointThroughPortal(drone, entrance, exit);
			vec1.Set(lookout.x - this.lookRange, lookout.y - this.lookRange);
			vec2.Set(lookout.x + this.lookRange, lookout.y + this.lookRange);
			const body = game.editor.queryWorldForBodies(vec1, vec2).find(candidate => this.isChaseTarget(candidate));
			if(!body) continue;
			const target = body.GetPosition();
			const seen = mapPointThroughPortal({x: target.x, y: target.y}, exit, entrance);
			const hit = rayCastThroughPortals(Box2D, game.world, drone, seen, {
				acceptFixture: fixture => fixture.GetBody() !== this.body,
				maxCrossings: 1,
			});
			const hitBody = hit?.fixture?.GetBody();
			if(hit?.crossings.length && hit.crossings[0].source === entrance && hitBody.mySprite && body.mySprite && hitBody.mySprite.data.prefabInstanceName === body.mySprite.data.prefabInstanceName){
				this.chaseTarget = body;
				this.chasePortal = {entrance, exit};
				return true;
			}
		}
		return false;
	}

	portalOpen({entrance, exit}){
		return !entrance.destroyed && !exit.destroyed && entrance.bridge && !entrance.bridge.destroyed && entrance.bridge === exit.bridge;
	}

	// Where the target shows behind the entrance, moved along the portal so the
	// line to it crosses the opening clear of its rims.
	portalChasePoint(){
		const {entrance, exit} = this.chasePortal;
		const target = this.chaseTarget.GetPosition();
		const seen = mapPointThroughPortal({x: target.x, y: target.y}, exit, entrance);
		const position = this.body.GetPosition();
		const drone = {x: position.x, y: position.y};
		const portal = entrance.body.GetPosition();
		const angle = entrance.body.GetAngle();
		const droneDistance = signedPortalDistance(drone, portal, angle);
		const seenDistance = signedPortalDistance(seen, portal, angle);
		if(droneDistance <= 0 || seenDistance >= 0) return seen;
		const t = droneDistance / (droneDistance - seenDistance);
		const offset = portalTangentDistance({x: drone.x + (seen.x - drone.x) * t, y: drone.y + (seen.y - drone.y) * t}, portal, angle);
		const limit = Math.max(0, entrance.apertureHalfLength - PORTAL_CLEARANCE);
		const shift = (b2Clamp(offset, -limit, limit) - offset) / t;
		const tangent = portalTangent(angle);
		return {x: seen.x + tangent.x * shift, y: seen.y + tangent.y * shift};
	}

	// Out of the portal, the target is in plain sight; the next look confirms it.
	// The portal may have turned the drone over, so it rights itself.
	setTeleported(){
		this.chasePortal = null;
		this.righting = true;
	}

	// A linked portal's cross-line and the walls holding it are open inside its
	// opening: a rotor or saw touching one there, on its way in or through, hits nothing.
	inPortalOpening(fixture, other){
		const circle = Box2D.castObject(fixture.GetShape(), Box2D.b2CircleShape);
		const point = this.body.GetWorldPoint(circle.get_m_p());
		const center = {x: point.x, y: point.y};
		const opens = portal => endpointUsesHostFixture(Box2D, portal, other) || (other._portalBackstop && getPointer(other.GetBody()) === getPointer(portal.body));
		// Halfway through, the drone also meets the walls of the far portal.
		const candidate = this.body._portalTransitCandidate;
		if(candidate && (opens(candidate.source) || opens(candidate.target))) return pointWithinPortalAperture(center, candidate.source);
		return linkedPortals().some(portal => opens(portal) && pointWithinPortalAperture(center, portal) && signedPortalDistance(center, portal.body.GetPosition(), portal.body.GetAngle()) > 0);
	}

	positionEmitters(){
		const correctedPos = vec1;
		correctedPos.Set(this.body.GetPosition().x, this.body.GetPosition().y);
		const correctLengthOffset = 0.5;
		const correctionAngle = this.body.GetAngle()-Settings.pihalve;
		correctedPos.set_x(correctedPos.get_x() + correctLengthOffset * Math.cos(correctionAngle));
		correctedPos.set_y(correctedPos.get_y() + correctLengthOffset * Math.sin(correctionAngle));

		let pos = vec2;
		pos.Set(correctedPos.x, correctedPos.y);
        const lengthOffset = 2.1;
        let angle = this.body.GetAngle()+Math.PI;
        pos.set_x(pos.get_x() + lengthOffset * Math.cos(angle));
		pos.set_y(pos.get_y() + lengthOffset * Math.sin(angle));

        this.emitterLeft.spawnPos.set(pos.x * Settings.PTM, pos.y * Settings.PTM);
		let emitterAngleOffset = (this.emitterLeft.maxStartRotation - this.emitterLeft.minStartRotation) / 2;

        this.emitterLeft.minStartRotation = - emitterAngleOffset;
        this.emitterLeft.maxStartRotation = + emitterAngleOffset;
		this.emitterLeft.rotation = angle * game.editor.RAD2DEG;

		angle = this.body.GetAngle();

		pos.set_x(correctedPos.get_x() + lengthOffset * Math.cos(angle));
		pos.set_y(correctedPos.get_y() + lengthOffset * Math.sin(angle));

        this.emitterRight.spawnPos.set(pos.x * Settings.PTM, pos.y * Settings.PTM);
        emitterAngleOffset = (this.emitterRight.maxStartRotation - this.emitterRight.minStartRotation) / 2;
        this.emitterRight.minStartRotation = - emitterAngleOffset;
        this.emitterRight.maxStartRotation = + emitterAngleOffset;
        this.emitterRight.rotation = angle * game.editor.RAD2DEG;

	}
	destroy(){
		emitterManager.destroyEmitter(this.emitterLeft);
		emitterManager.destroyEmitter(this.emitterRight);
        delete this.emitterLeft;
        delete this.emitterRight;


		Box2D.destroy(this.lookRangeVec);
		Box2D.destroy(this.startPosition);

		if(this.wayPoint) Box2D.destroy(this.wayPoint);

        super.destroy();
	}

	initContactListener() {
        super.initContactListener();
        var self = this;
        this.contactListener.BeginContact = function (contact) {
			const sideFixture = self.sides.includes(contact.GetFixtureA()) ? contact.GetFixtureA() : (self.sides.includes(contact.GetFixtureB()) ? contact.GetFixtureB() : null);


			if(sideFixture && !self.destroyed){

				const otherFixture = sideFixture === contact.GetFixtureA() ? contact.GetFixtureB() : contact.GetFixtureA();
				if(otherFixture.IsSensor() || self.inPortalOpening(sideFixture, otherFixture)) return;

				const isFlesh = otherFixture.GetBody().isFlesh;
				const pushForce = 300 / game.editor.editorSettingsObject.gameSpeed;
				const angleVector = vec4;
				angleVector.Set(Math.cos(self.body.GetAngle()), Math.sin(self.body.GetAngle()));

				if(sideFixture === self.sides[SIDE_RIGHT]){
					const pos = vec1;
					pos.Set(self.emitterRight.spawnPos.x, self.emitterRight.spawnPos.y);
					b2MulVec2(pos, 1/game.editor.PTM);
					if(isFlesh){
						emitterManager.playOnceEmitter("blood", otherFixture.GetBody(), pos, self.emitterRight.rotation);
						game.editor.queueDecalToBody(otherFixture.GetBody(), pos, "Decal.png", true, 1.0);
					}else self.emitterRight.playOnce();

					let force1 = vec2;
					force1.Set(angleVector.x, angleVector.y);
					b2MulVec2(force1, -pushForce/4);

					let force2 = vec3;
					force2.Set(angleVector.x, angleVector.y);
					b2MulVec2(force2, -pushForce);

					self.body.ApplyForce(force1, pos, true);
					self.body.ApplyForce(force2, self.body.GetPosition(), true);

					AudioManager.playSFX('drone-wall', 0.3, 1.0 + 0.4 * Math.random()-0.2, self.body.GetPosition());

				} else if(sideFixture === self.sides[SIDE_LEFT]){
					const pos = vec1;
					pos.Set(self.emitterLeft.spawnPos.x, self.emitterLeft.spawnPos.y)
					b2MulVec2(pos, 1/Settings.PTM);

					if(isFlesh){
						emitterManager.playOnceEmitter("blood", otherFixture.GetBody(), pos, self.emitterLeft.rotation);
						game.editor.queueDecalToBody(otherFixture.GetBody(), pos, "Decal.png", true, 1.0);
					}else self.emitterLeft.playOnce();

					let force1 = vec2;
					force1.Set(angleVector.x, angleVector.y);
					b2MulVec2(force1, pushForce/4);

					let force2 = vec3;
					force2.Set(angleVector.x, angleVector.y);
					b2MulVec2(force2, pushForce);

					self.body.ApplyForce(force1, pos, true);
					self.body.ApplyForce(force2, self.body.GetPosition(), true);

					AudioManager.playSFX('drone-wall', 0.3, 1.0 + 0.4 * Math.random()-0.2, self.body.GetPosition());
				}

				if(self.activateOn == Explosive.activateOnTypes.mainCharacter){
					if(otherFixture.GetBody().mainCharacter){
						self.set('active', true);
					}
				}else if(self.activateOn == Explosive.activateOnTypes.anyCharacter){
					if(otherFixture.GetBody().isHumanoid){
						self.set('active', true);
					}
				}
			}
        }
        this.contactListener.EndContact = function (contact) {
		}
	}
}


DroneBomb.chaseTypes = {
	none:"None",
	mainCharacter:"Main Character",
	anyCharacter:"Any Character"
}
DroneBomb.settings = Object.assign({}, Explosive.settings, {
	"activateOn": Explosive.activateOnTypes.mainCharacter,
	"chase": DroneBomb.chaseTypes.mainCharacter,
	"lookRange": 20,
	"flySpeed": 100,
});
DroneBomb.settingsOptions = Object.assign({}, Explosive.settingsOptions, {
	"chase":Object.values(DroneBomb.chaseTypes),
	"lookRange": {
        min: 5,
        max: 100,
        step: 1
	},
	"flySpeed": {
        min: 50,
        max: 500,
        step: 1
	},
});


PrefabManager.prefabLibrary.DroneBomb = {
    json: JSON.stringify({"objects":[[0,0.01,-0.051,0,"dronebomb","body",0,["#999999","#999999","#999999","#999999"],["#000","#000","#000","#000"],[0,0,0,0,0],false,true,[[{"x":-2.1,"y":0.16},{"x":-2.1,"y":-0.476},{"x":2.096,"y":-0.476},{"x":2.096,"y":0.16}],[{"x":-0.845,"y":1.536},{"x":-0.845,"y":0.098},{"x":0.801,"y":0.098},{"x":0.801,"y":1.536}],[{"x":-2.199,"y":-0.33},{"x":-2.199,"y":-0.33}],[{"x":2.222,"y":-0.33},{"x":2.222,"y":-0.33}]],[0.3,0.3,0.1,0.1],0,[0,0,6.651,6.651],"",[1,1,1,1],true,false,false,[0.5,0.5,0.5,0.5],[0.2,0.2,0.2,0.2]],[7,-0.292,1.533,0,"","",1,["[1,-0.432,-13.525,0,\"\",\"\",1,\"HommingDrone_Light0000\",null,null,null,null,false,\"#FFFFFF\",1,1,1,0,0,0,true]","[1,0.432,13.525,0,\"dronebomb\",\"texture\",2,\"HommingBomb_Drone_off0000\",null,null,null,null,false,\"#FFFFFF\",1,1,1,0,0,0,true]"],0,3.12,-1.759,0,1,0,0,0,true]]}),
    class: DroneBomb,
    library: PrefabManager.LIBRARY_WEAPON
}
